// src/api/index.js
import { logger } from '../utils/logger';

const USE_MOCKS = process.env.REACT_APP_USE_MOCK === 'true';

/**
 * Canonical API base URL. Resolution order:
 *  1. REACT_APP_BACKEND_URL environment variable  (set this in production)
 *  2. Android emulator address when running as a Capacitor native app
 *  3. http://localhost:8000 for local development
 *
 * In production builds, set REACT_APP_BACKEND_URL=https://api.yourdomain.com
 * via your CI secret / hosting platform — never hardcode it here.
 */
export const API_BASE = USE_MOCKS
  ? ''
  : process.env.REACT_APP_BACKEND_URL
    || (window.Capacitor?.isNativePlatform?.() ? 'http://10.0.2.2:8000' : (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : ''));

const mockResponses = {
  '/api/organizations/': () => [
    { id: 1, name: 'Green Earth Foundation' },
    { id: 2, name: 'Hope for Kids Trust' },
  ],
  '/api/organizations/create/': (body) => {
    const data = JSON.parse(body);
    return {
      id: 99,
      name: data.name,
      reg_id: data.reg_id,
      message: 'Organization created successfully',
    };
  },
};

export async function apiCall(path, options = {}, getToken) {
  if (USE_MOCKS) {
    await new Promise(r => setTimeout(r, 600)); // simulate network delay
    const mockFn = mockResponses[path];
    if (mockFn) return mockFn(options.body);
    // eslint-disable-next-line no-throw-literal
    throw { status: 404, detail: `No mock defined for ${path}` };
  }

  const method = options.method || 'GET';
  logger.info('API', 'Request started', { method, url: path });

  let token;
  try {
    token = await getToken({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
  } catch (error) {
    logger.error('Auth0', 'Token retrieval failed', { errorMessage: error.message });
    throw error;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch (error) {
    logger.network('API', 'Backend unreachable — is the Django server running?', { url: path, method });
    // eslint-disable-next-line no-throw-literal
    throw { type: 'NETWORK_ERROR', status: 0, detail: 'networkError' };
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    
    if (res.status >= 500) {
      logger.network('API', 'Backend responded but may be having a database issue', { url: path, status: res.status, statusText: res.statusText });
    } else if (res.status === 401 || res.status === 403) {
      logger.warn('API', 'Auth/permission error — check Auth0 token audience', { url: path, status: res.status });
    } else if (res.status === 400 && (err.detail || err.error)) {
      logger.warn('API', err.detail || err.error, { code: err.code, url: path });
    }
    
    // eslint-disable-next-line no-throw-literal
    throw { status: res.status, ...err };
  }
  
  logger.info('API', 'Request succeeded', { method, url: path, status: res.status });
  
  if (res.status === 204) {
    return null;
  }
  
  return res.json();
}

/**
 * Download a binary payload (PDF/Excel) from the backend.
 *
 * Mirrors `apiCall` for auth + error logging, but returns the raw blob and a
 * suggested filename parsed from the Content-Disposition header instead of
 * parsing JSON. Error responses (400/403/429) are still JSON and are thrown
 * with their status so callers can render specific messages — the 429 case
 * also carries `retryAfter` (seconds from the Retry-After header).
 */
export async function apiDownload(path, options = {}, getToken) {
  if (USE_MOCKS) {
    await new Promise(r => setTimeout(r, 300));
    // eslint-disable-next-line no-throw-literal
    throw { status: 404, detail: `No mock defined for ${path}` };
  }

  const method = options.method || 'GET';
  logger.info('API', 'Download requested', { method, url: path });

  let token;
  try {
    token = await getToken({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } });
  } catch (error) {
    logger.error('Auth0', 'Token retrieval failed', { errorMessage: error.message });
    throw error;
  }

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  } catch (error) {
    logger.network('API', 'Backend unreachable while downloading', { url: path, method });
    // eslint-disable-next-line no-throw-literal
    throw { type: 'NETWORK_ERROR', status: 0, detail: 'networkError' };
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));

    if (res.status === 429) {
      const retryAfter = Number(res.headers.get('Retry-After')) || null;
      logger.warn('API', 'Request throttled', { url: path, status: res.status, retryAfter });
      // eslint-disable-next-line no-throw-literal
      throw { status: res.status, retryAfter, ...err };
    }

    if (res.status >= 500) {
      logger.network('API', 'Backend responded but may be having a database issue', { url: path, status: res.status, statusText: res.statusText });
    } else if (res.status === 401 || res.status === 403) {
      logger.warn('API', 'Auth/permission error — check Auth0 token audience', { url: path, status: res.status });
    } else if (res.status === 400 && (err.detail || err.error)) {
      logger.warn('API', err.detail || err.error, { code: err.code, url: path });
    }

    // eslint-disable-next-line no-throw-literal
    throw { status: res.status, ...err };
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : null;

  logger.info('API', 'Download succeeded', { method, url: path, status: res.status, filename });
  return { blob, filename, contentType: res.headers.get('Content-Type') };
}